export class AcademicProfileResume {
    constructor(
        public contentsInProgress: number,
        public completedContents: number,
        public totalSubscribedContents: number,
        public data: ResumeData
    ) { }

    static fromJson(json: any): AcademicProfileResume {
        return new AcademicProfileResume(
            json.contentsInProgress,
            json.completedContents,
            json.totalSubscribedContents,
            ResumeData.fromJson(json.data)
        );
    }
}

export class CalendarEvents {
    constructor(
        public events: any[],
    ) { }

    static fromJson(json: any): CalendarEvents {
        return new CalendarEvents(
            json.events
        );
    }
}

export class ResumeData {
    constructor(
        public certificatesObtained: CertificateSection,
        public catalogInteraction: CatalogInteractionSection,
        public registeredCourses: RegisteredContentSection<CourseData>,
        public registeredExternalContents: RegisteredContentSection<ExternalContentData>,
        public registeredTrajects: RegisteredContentSection<TrajectData>
    ) { }

    static fromJson(data: any): ResumeData {
        return new ResumeData(
            CertificateSection.fromJson(data.certificatesObtained),
            CatalogInteractionSection.fromJson(data.catalogInteraction),
            RegisteredContentSection.fromJson<CourseData>(data.registeredCourses, CourseData),
            RegisteredContentSection.fromJson<ExternalContentData>(data.registeredExternalContents, ExternalContentData),
            RegisteredContentSection.fromJson<TrajectData>(data.registeredTrajects, TrajectData)
        );
    }
}

// Aquí defines las secciones, puedes refinar estos tipos aún más:
export class CertificateSection {
    constructor(public totalCertificates: number, public data: Certificate[]) { }

    static fromJson(json: any): CertificateSection {
        return new CertificateSection(
            json?.totalCertificates ?? 0,
            (json?.data || []).map((item: any) => new Certificate(item))
        );
    }
}

/**
 * Certificado emitido, tal como lo devuelve el API desde course_progress_histories (fuente
 * única del certificado). `context` es 'course', 'external_content' o 'traject'.
 */
export class Certificate {
    context: string;
    contentId: number;
    contentName: string;
    contentImage: string;
    providerId: number;
    providerName: string;
    providerSlug: string;
    progress: number;
    doneAt: string;
    certificatedAt: string;
    certificatePath: string;

    constructor(data: any) {
        this.context = data.context;
        this.contentId = data.contentId;
        this.contentName = data.contentName;
        this.contentImage = data.contentImage;
        this.providerId = data.providerId;
        this.providerName = data.providerName;
        this.providerSlug = data.providerSlug;
        this.progress = data.progress;
        this.doneAt = data.doneAt;
        this.certificatedAt = data.certificatedAt;
        this.certificatePath = data.certificatePath;
    }
}

export class CatalogInteractionSection {
    constructor(public data: CatalogInteraction[]) { }

    static fromJson(json: any): CatalogInteractionSection {
        return new CatalogInteractionSection(json.data.map((item: any) => new CatalogInteraction(
            item.providerId,
            item.providerName,
            item.providerSlug,
            item.subscribedContents,
            item.completedContents,
            item.completedPercentage
        )));
    }
}

export class CatalogInteraction {
    constructor(
        public providerId: number,
        public providerName: string,
        public providerSlug: string,
        public subscribedContents: number,
        public completedContents: number,
        public completedPercentage: number
    ) { }
    // agrega los campos necesarios
}

export class RegisteredContentSection<T> {
    constructor(public data: T[]) { }

    static fromJson<U>(json: any, ctor: new (data: any) => U): RegisteredContentSection<U> {
        return new RegisteredContentSection(json.data.map((item: any) => new ctor(item)));
    }
}

export class CourseData {
    contentId: number;
    contentName: string;
    contentImage: string;
    providerId: number;
    providerName: string;
    providerSlug: string;
    progress: number;
    doneAt: string;
    certificatedAt: string;
    certificatePath: string;

    constructor(data: any) {
        this.contentId = data.contentId;
        this.contentName = data.contentName;
        this.contentImage = data.contentImage;
        this.providerId = data.providerId;
        this.providerName = data.providerName;
        this.providerSlug = data.providerSlug;
        this.progress = data.progress;
        this.doneAt = data.doneAt;
        this.certificatedAt = data.certificatedAt;
        this.certificatePath = data.certificatePath;
    }
}


export class ExternalContentData {
    contentId: number;
    contentName: string;
    contentImage: string;
    providerName: string;
    progress: number;
    doneAt: string;
    // Cuando la compañía certifica automáticamente (Xertify, POK) el contenido externo también
    // emite certificado. Sin mapearlos aquí, buildCertificates() no encontraba ni uno: el
    // certificado existe en course_progress_histories, pero se perdía al deserializar.
    certificatedAt: string;
    certificatePath: string;
    constructor(data: any) {
        this.contentId = data.contentId;
        this.contentName = data.contentName;
        this.contentImage = data.contentImage;
        this.providerName = data.providerName;
        this.progress = data.progress;
        this.doneAt = data.doneAt;
        this.certificatedAt = data.certificatedAt;
        this.certificatePath = data.certificatePath;
    }

}

export class TrajectData {
    trajectId: number;
    trajectName: string;
    trajectImage: string;
    contents: number;
    providerName: string;
    progress: number;
    doneAt: string;
    // Mismo caso que ExternalContentData: las rutas certificadas traen su certificado desde
    // course_progress_histories y hasta ahora se descartaba al construir el modelo.
    certificatedAt: string;
    certificatePath: string;
    constructor(data: any) {
        this.trajectId = data.trajectId;
        this.trajectName = data.trajectName;
        this.trajectImage = data.trajectImage;
        this.contents = data.contents;
        this.providerName = data.providerName;
        this.progress = data.progress;
        this.doneAt = data.doneAt;
        this.certificatedAt = data.certificatedAt;
        this.certificatePath = data.certificatePath;
    }
}
